<template>
  <div class="w-full p-4">
    <div class="flex flex-row justify-between items-center pb-2 border-b border-b-gray-400 mb-4">
      <h2 class="text-lg font-semibold">Expense Reports</h2>
    </div>

    <!-- Tabs -->
    <div class="flex flex-row gap-1 mb-3 text-sm border-b border-gray-300">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        class="px-3 py-1.5 -mb-px border-b-2"
        :class="activeTab === tab.key
          ? 'border-accent-primary text-accent-primary font-semibold'
          : 'border-transparent text-gray-500 hover:text-dark-primary'"
        @click="activeTab = tab.key"
      >{{ tab.label }}</button>
    </div>

    <!-- Shared filters -->
    <div class="flex flex-wrap gap-3 mb-3 items-end text-sm">
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">From</label>
        <input v-model="filters.start" type="date" class="p-1.5 border border-gray-400 rounded-md" />
      </div>
      <div>
        <label class="block text-xs text-gray-500 mb-0.5">To</label>
        <input v-model="filters.end" type="date" class="p-1.5 border border-gray-400 rounded-md" />
      </div>
      <div v-if="activeTab === 'status'">
        <label class="block text-xs text-gray-500 mb-0.5">View</label>
        <select v-model="filters.view" class="p-1.5 border border-gray-400 rounded-md">
          <option value="submitter">Submitter</option>
          <option value="client">Client Collection</option>
        </select>
      </div>
      <div v-if="activeTab === 'audit'">
        <label class="block text-xs text-gray-500 mb-0.5">Expense ID (optional)</label>
        <input v-model="filters.record_id" type="number" class="p-1.5 border border-gray-400 rounded-md" />
      </div>
      <button
        class="px-3 py-1.5 text-sm rounded border border-gray-400 hover:bg-gray-100"
        @click="generate"
        :disabled="loading"
      >{{ loading ? 'Generating...' : 'Generate' }}</button>
      <button
        class="px-3 py-1.5 text-sm rounded bg-accent-primary text-white hover:bg-accent-secondary"
        @click="downloadCsv"
      >Download CSV</button>
    </div>

    <div v-if="error" class="p-2 rounded bg-red-50 border border-red-200 text-sm text-red-700 mb-3">
      {{ error }}
    </div>

    <div v-if="data" class="border border-gray-400 rounded-md p-4 bg-white">
      <!-- Payroll -->
      <div v-if="activeTab === 'payroll'">
        <div class="text-base font-semibold mb-2">Payroll Reimbursement — Grand Total: ${{ formatAmount(data.grand_total) }}</div>
        <div v-for="block in data.submitters" :key="block.submitter.id" class="mb-4">
          <div class="text-sm font-semibold">{{ block.submitter.full_name }} — subtotal ${{ formatAmount(block.subtotal) }}</div>
          <table class="table-fixed w-full text-sm mt-1">
            <colgroup>
              <col class="w-28" />
              <col class="w-24" />
              <col class="w-40" />
              <col />
              <col class="w-64" />
              <col class="w-20" />
            </colgroup>
            <thead><tr class="bg-gray-100">
              <th class="text-left px-2 py-1">Date</th>
              <th class="text-right px-2 py-1">Amount</th>
              <th class="text-left px-2 py-1">Category</th>
              <th class="text-left px-2 py-1">Description</th>
              <th class="text-left px-2 py-1">Client/Engagement</th>
              <th class="text-center px-2 py-1">Receipts</th>
            </tr></thead>
            <tbody>
              <tr v-for="r in block.records" :key="r.id" class="border-t border-gray-200">
                <td class="px-2 py-1">{{ r.date_of_expense }}</td>
                <td class="px-2 py-1 text-right">${{ formatAmount(r.amount) }}</td>
                <td class="px-2 py-1 break-words">{{ r.category }}</td>
                <td class="px-2 py-1 break-words">{{ r.description }}</td>
                <td class="px-2 py-1 break-words">
                  <template v-if="r.is_client_reimbursable">{{ r.client_name }} / {{ r.engagement_title }}</template>
                  <span v-else class="text-gray-400">—</span>
                </td>
                <td class="px-2 py-1 text-center">{{ r.receipt_count }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- AR -->
      <div v-if="activeTab === 'ar'">
        <div class="text-base font-semibold mb-2">Accounts Receivable — Grand Total: ${{ formatAmount(data.grand_total) }}</div>
        <div v-for="cb in data.clients" :key="cb.client.id" class="mb-4">
          <div class="text-sm font-semibold">{{ cb.client.name }} — ${{ formatAmount(cb.total) }}</div>
          <div v-for="eb in cb.engagements" :key="eb.engagement.id" class="ml-2 mb-2">
            <div class="text-xs text-gray-600">{{ eb.engagement.title }} — subtotal ${{ formatAmount(eb.subtotal) }}</div>
            <table class="table-fixed w-full text-sm">
              <colgroup>
                <col class="w-28" />
                <col class="w-24" />
                <col class="w-44" />
                <col />
              </colgroup>
              <thead><tr class="bg-gray-100">
                <th class="text-left px-2 py-1">Date</th>
                <th class="text-right px-2 py-1">Amount</th>
                <th class="text-left px-2 py-1">Submitter</th>
                <th class="text-left px-2 py-1">Description</th>
              </tr></thead>
              <tbody>
                <tr v-for="r in eb.records" :key="r.id" class="border-t border-gray-200">
                  <td class="px-2 py-1">{{ r.date_of_expense }}</td>
                  <td class="px-2 py-1 text-right">${{ formatAmount(r.amount) }}</td>
                  <td class="px-2 py-1 break-words">{{ r.submitter_name }}</td>
                  <td class="px-2 py-1 break-words">{{ r.description }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Bank Reconciliation -->
      <div v-if="activeTab === 'bank-reconciliation'">
        <div class="text-base font-semibold mb-2">Bank Reconciliation — Grand Total: ${{ formatAmount(data.grand_total) }}</div>
        <div class="text-xs text-gray-600 mb-2">Company-card expenses approved by Accounting and not yet reconciled against the bank statement.</div>
        <table class="table-fixed w-full text-sm">
          <colgroup>
            <col class="w-28" />
            <col class="w-24" />
            <col class="w-40" />
            <col class="w-40" />
            <col />
            <col class="w-64" />
            <col class="w-20" />
          </colgroup>
          <thead><tr class="bg-gray-100">
            <th class="text-left px-2 py-1">Date</th>
            <th class="text-right px-2 py-1">Amount</th>
            <th class="text-left px-2 py-1">Submitter</th>
            <th class="text-left px-2 py-1">Category</th>
            <th class="text-left px-2 py-1">Description</th>
            <th class="text-left px-2 py-1">Client/Engagement</th>
            <th class="text-center px-2 py-1">Receipts</th>
          </tr></thead>
          <tbody>
            <tr v-for="r in data.records" :key="r.id" class="border-t border-gray-200">
              <td class="px-2 py-1">{{ r.date_of_expense }}</td>
              <td class="px-2 py-1 text-right">${{ formatAmount(r.amount) }}</td>
              <td class="px-2 py-1 break-words">{{ r.submitter_name }}</td>
              <td class="px-2 py-1 break-words">{{ r.category }}</td>
              <td class="px-2 py-1 break-words">{{ r.description }}</td>
              <td class="px-2 py-1 break-words">
                <template v-if="r.is_client_reimbursable">{{ r.client_name }} / {{ r.engagement_title }}</template>
                <span v-else class="text-gray-400">—</span>
              </td>
              <td class="px-2 py-1 text-center">{{ r.receipt_count }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Reimbursement Status -->
      <div v-if="activeTab === 'status' && data.view === 'submitter'">
        <div v-for="sb in data.submitters" :key="sb.submitter.id" class="mb-4">
          <div class="text-sm font-semibold">{{ sb.submitter.full_name }} — Reimbursed ${{ formatAmount(sb.reimbursed) }} · Outstanding ${{ formatAmount(sb.outstanding) }}</div>
          <table class="table-fixed w-full text-sm mt-1">
            <colgroup>
              <col class="w-28" />
              <col class="w-24" />
              <col />
              <col class="w-32" />
              <col class="w-44" />
              <col class="w-28" />
            </colgroup>
            <thead><tr class="bg-gray-100">
              <th class="text-left px-2 py-1">Date</th>
              <th class="text-right px-2 py-1">Amount</th>
              <th class="text-left px-2 py-1">Description</th>
              <th class="text-left px-2 py-1">Status</th>
              <th class="text-left px-2 py-1">Payroll Run</th>
              <th class="text-left px-2 py-1">Paid Date</th>
            </tr></thead>
            <tbody>
              <tr v-for="r in sb.records" :key="r.id" class="border-t border-gray-200">
                <td class="px-2 py-1">{{ r.date_of_expense }}</td>
                <td class="px-2 py-1 text-right">${{ formatAmount(r.amount) }}</td>
                <td class="px-2 py-1 break-words">{{ r.description }}</td>
                <td class="px-2 py-1">{{ r.submitter_reimbursed ? 'Reimbursed' : 'Outstanding' }}</td>
                <td class="px-2 py-1 break-words">{{ r.payroll_batch_reference || '—' }}</td>
                <td class="px-2 py-1">{{ r.submitter_reimbursed_at || '—' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="activeTab === 'status' && data.view === 'client'">
        <div v-for="cb in data.clients" :key="cb.client.id" class="mb-4">
          <div class="text-sm font-semibold">{{ cb.client.name }} — Collected ${{ formatAmount(cb.collected) }} · Outstanding ${{ formatAmount(cb.outstanding) }}</div>
          <table class="table-fixed w-full text-sm mt-1">
            <colgroup>
              <col />
              <col class="w-28" />
              <col class="w-24" />
              <col class="w-44" />
              <col class="w-44" />
              <col class="w-32" />
            </colgroup>
            <thead><tr class="bg-gray-100">
              <th class="text-left px-2 py-1">Engagement</th>
              <th class="text-left px-2 py-1">Date</th>
              <th class="text-right px-2 py-1">Amount</th>
              <th class="text-left px-2 py-1">Submitter</th>
              <th class="text-left px-2 py-1">Invoice Ref</th>
              <th class="text-left px-2 py-1">Status</th>
            </tr></thead>
            <tbody>
              <tr v-for="r in cb.records" :key="r.id" class="border-t border-gray-200">
                <td class="px-2 py-1 break-words">{{ r.engagement_title }}</td>
                <td class="px-2 py-1">{{ r.date_of_expense }}</td>
                <td class="px-2 py-1 text-right">${{ formatAmount(r.amount) }}</td>
                <td class="px-2 py-1 break-words">{{ r.submitter_name }}</td>
                <td class="px-2 py-1 break-words">{{ r.client_invoice_reference || '—' }}</td>
                <td class="px-2 py-1">{{ r.client_paid ? 'Collected' : 'Outstanding' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Audit -->
      <div v-if="activeTab === 'audit'">
        <div class="text-base font-semibold mb-2">Audit trail ({{ data.transitions ? data.transitions.length : 0 }})</div>
        <table class="table-auto w-full text-sm">
          <thead><tr class="bg-gray-100">
            <th class="text-left px-2 py-1">Timestamp</th>
            <th class="text-left px-2 py-1">Expense ID</th>
            <th class="text-left px-2 py-1">Action</th>
            <th class="text-left px-2 py-1">From</th>
            <th class="text-left px-2 py-1">To</th>
            <th class="text-left px-2 py-1">Actor</th>
            <th class="text-left px-2 py-1">Reason</th>
          </tr></thead>
          <tbody>
            <tr v-for="t in data.transitions" :key="t.id" class="border-t border-gray-200">
              <td class="px-2 py-1 whitespace-nowrap">{{ formatDateTime(t.created_at) }}</td>
              <td class="px-2 py-1">{{ t.expense_record_id }}</td>
              <td class="px-2 py-1">{{ t.action }}</td>
              <td class="px-2 py-1">{{ t.from_status || '—' }}</td>
              <td class="px-2 py-1">{{ t.to_status || '—' }}</td>
              <td class="px-2 py-1">{{ t.actor }}</td>
              <td class="px-2 py-1">{{ t.reason || '' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="!data && !loading" class="text-sm text-gray-500 mt-3">
      Set filters and click Generate.
    </div>
  </div>
</template>

<script>
export default { name: "expense-reports" };
</script>

<script setup>
import { ref, reactive, computed } from "vue";
import { useStore } from "vuex";
import { useToast } from "vue-toast-notification";
import { getExpenseReport, expenseReportCsvUrl } from "@/services/Expense.js";

const store = useStore();
const $toast = useToast();

const tabs = [
  { key: 'payroll', label: 'Payroll Reimbursement' },
  { key: 'ar', label: 'Accounts Receivable' },
  { key: 'bank-reconciliation', label: 'Bank Reconciliation' },
  { key: 'status', label: 'Reimbursement Status' },
  { key: 'audit', label: 'Audit' },
];

const activeTab = ref('payroll');
const loading = ref(false);
const data = ref(null);
const error = ref('');

const filters = reactive({
  start: null,
  end: null,
  view: 'submitter',
  record_id: null,
});

function formatAmount(n) {
  if (n === null || n === undefined) return "0.00";
  const num = typeof n === "string" ? parseFloat(n) : n;
  return num.toFixed(2);
}

function formatDateTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString();
}

function buildQueryParams() {
  const params = new URLSearchParams();
  if (filters.start) params.append('start', filters.start);
  if (filters.end) params.append('end', filters.end);
  if (activeTab.value === 'status' && filters.view) params.append('view', filters.view);
  if (activeTab.value === 'audit' && filters.record_id) params.append('record_id', filters.record_id);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

async function generate() {
  loading.value = true;
  error.value = '';
  data.value = null;
  const token = store.state.auth.token;
  try {
    data.value = await getExpenseReport(token, activeTab.value, buildQueryParams());
  } catch (err) {
    error.value = (err.response && err.response.data && JSON.stringify(err.response.data))
      || err.message || 'Failed to load report.';
  }
  loading.value = false;
}

function downloadCsv() {
  const url = expenseReportCsvUrl(activeTab.value, buildQueryParams());
  const token = store.state.auth.token;
  // Auth via Token header isn't available on a simple <a> link. Fetch with auth then blob-download.
  fetch(url, { headers: { 'Authorization': `Token ${token}` } })
    .then((r) => r.blob())
    .then((blob) => {
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = `${activeTab.value}.csv`;
      a.click();
      URL.revokeObjectURL(objUrl);
    })
    .catch(() => $toast.open({ message: 'CSV download failed', type: 'error', duration: 3000 }));
}
</script>
